import { useEffect, useEffectEvent, useId, useRef, useState } from 'react'
import { useTranslation } from '@/i18n'

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let turnstileScriptPromise: Promise<void> | null = null

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>,
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (turnstileScriptPromise) return turnstileScriptPromise

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`,
    )

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Turnstile script kon niet worden geladen.')),
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Turnstile script kon niet worden geladen.'))
    document.head.appendChild(script)
  })

  return turnstileScriptPromise
}

export function TurnstileWidget({
  siteKey,
  resetKey = 0,
  action,
  onTokenChange,
  onError,
}: {
  siteKey: string
  resetKey?: number
  action?: string
  onTokenChange: (token: string | null) => void
  onError?: (message: string) => void
}) {
  const widgetContainerRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const widgetIdRef = useRef<string | null>(null)
  const widgetInstanceId = useId()
  const [scriptState, setScriptState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const handleTokenChange = useEffectEvent((token: string | null) => {
    onTokenChange(token)
  })
  const handleError = useEffectEvent((message: string) => {
    onError?.(message)
  })

  useEffect(() => {
    let cancelled = false

    setScriptState('loading')
    handleTokenChange(null)

    void loadTurnstileScript()
      .then(() => {
        if (cancelled) return
        if (!widgetContainerRef.current || !window.turnstile) {
          throw new Error('Turnstile unavailable')
        }

        widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
          sitekey: siteKey,
          theme: 'dark',
          action,
          callback: (token: string) => {
            handleTokenChange(token)
          },
          'expired-callback': () => {
            handleTokenChange(null)
          },
          'timeout-callback': () => {
            handleTokenChange(null)
          },
          'error-callback': (errorCode?: string) => {
            handleTokenChange(null)
            handleError(
              t('Turnstile kon je verificatie niet afronden. Probeer het opnieuw.') + (errorCode ? ` (${errorCode})` : ''),
            )
          },
        })

        setScriptState('ready')
      })
      .catch((error) => {
        if (cancelled) return
        setScriptState('error')
        handleTokenChange(null)
        handleError(
          t('Turnstile kon niet worden geladen.'),
        )
      })

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
    // handleTokenChange/handleError are useEffectEvent callbacks: always
    // stable in behavior but NOT in identity, so they must stay out of this
    // array or the widget remounts (and loses its token) on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, siteKey])

  useEffect(() => {
    if (resetKey === 0) return
    if (!widgetIdRef.current || !window.turnstile) return

    handleTokenChange(null)
    window.turnstile.reset(widgetIdRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  return (
    <div className="rounded-[1.15rem] border border-white/[0.07] bg-[#111719] p-4">
      <p className="text-sm font-semibold text-white">{t('Anti-bot controle')}</p>
      <p className="mt-1 text-xs leading-5 text-surface-500">
        {t('Bevestig eerst dat je geen geautomatiseerd verzoek verstuurt.')}
      </p>

      <div className="mt-4 min-h-[72px]">
        <div
          id={`turnstile-${widgetInstanceId}`}
          ref={widgetContainerRef}
          className="rounded-xl bg-[#111719]"
        />
      </div>

      {scriptState === 'loading' ? (
        <p className="mt-3 text-xs text-surface-500">{t('Turnstile laden…')}</p>
      ) : null}
    </div>
  )
}
